import React, { useState, useMemo } from 'react';
import { FaQuestionCircle, FaChevronDown, FaChevronUp, FaSearch } from 'react-icons/fa';
import './Help.css';

const Help = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const faqData = [
    {
      category: 'Getting Started',
      questions: [
        {
          question: '⚙️ What is this tool?',
          answer: 'Victorise synchronizes video and different kinds of data (braking, GPS, speed, suspension, etc.). You can overlay your metrics directly on your videos to analyze your performance, create segments, compare runs, and share insights with your clubs.'
        },
        {
          question: '🤔 What file formats are supported?',
          answer: 'Our tool supports multiple data formats: CSV files from Brake Ace Desktop (braking, GPS, speed), CSV from BYB data acquisition (suspension, acceleration, gyro), FIT files (Garmin, Wahoo), GPX files, and video formats like MP4 and AVI. For GoPro videos, we can extract embedded telemetry data directly.'
        },
        {
          question: '📹 How can I use data from my GoPro camera?',
          answer: 'You can acquire data from your GoPro in two ways: using our online extraction tool (accessible from "Our Tools" in the sidebar), or by using our desktop tool. The online tool extracts data directly from your uploaded file, while the desktop tool processes files locally—ideal for large files and faster performance.'
        },
        {
          question: 'How do I upload my first run?',
          answer: 'Go to the Home page and use the drop zone to upload your FIT file, CSV data, or video. You can also navigate to "My Runs" and click the "New Run" button. Upload both video and telemetry data together for the best synchronized analysis experience.'
        }
      ]
    },
    {
      category: 'My Runs',
      questions: [
        {
          question: 'How do I view and manage my runs?',
          answer: 'Go to "My Runs" in the sidebar to see all your runs. You can search by name, filter by "All", "With video", or "With telemetry", and sort by date, name, duration, distance, or TSS. Switch between grid and list view. Use run groups to organize runs, and drag runs from My Runs onto the Calendar to assign them to a day.'
        },
        {
          question: '📁 How do I save and load runs?',
          answer: 'In the run (Ride) view, click the save button to store your run in the cloud (Firebase). All your runs are automatically saved and accessible from "My Runs". Use search, filter, and sort to find specific runs.'
        },
        {
          question: 'How do I create a new run?',
          answer: 'Click "New Run" on My Runs or drop files on the Home drop zone. You can add a name, description, video (file or YouTube URL), and telemetry (upload CSV/FIT/GPX or import from Strava or Brake Ace if connected). Save to store in the cloud.'
        }
      ]
    },
    {
      category: 'Ride View (run detail)',
      questions: [
        {
          question: 'What panels are available in the Ride view?',
          answer: 'The Ride view shows Video, Graph (telemetry chart), Map (GPS), Stats (summary and training zones), and Comments. Use the toggles at the top to show or hide each panel. All views stay in sync with the playback time.'
        },
        {
          question: 'Can I change the layout of the Ride view?',
          answer: 'Yes. Use the layout dropdown to resize panels by dragging the dividers. You can save your layout and load it later so your preferred arrangement is restored.'
        },
        {
          question: '📝 How do I see detailed metrics and jump to a moment in the video?',
          answer: 'The telemetry chart shows speed, brake pressure, heart rate, power, elevation, and more. Click any point on the chart to jump to that moment in the video and on the map. If you have training zones configured (Profile or Calendar), the Stats panel can show time in zones.'
        },
        {
          question: 'How do I add or merge more metrics into a run?',
          answer: 'In the Ride view, you can add metrics from another CSV file (e.g. heart rate or power from a second device). Use "Add metrics" in the chart area; the app will ask you to choose for each stream whether to keep current data, replace with the new file, or import only new metrics.'
        },
        {
          question: 'What if my video and data start at different times?',
          answer: 'Use the sync offset in the run settings (or in the Ride view) to shift the video or data in time. This aligns playback when your camera and GPS/sensors had different start times. The Video Sync Test tool (in Our Tools) can help fine-tune sync.'
        },
        {
          question: 'Can I export a video with overlays?',
          answer: 'Yes. From the Ride view you can export a video with your metrics overlaid. This uses credits; your balance is shown in the sidebar. The export runs on our servers and you can track progress in the app.'
        },
        {
          question: 'Can I add comments to specific moments?',
          answer: 'Yes. Turn on the Comments panel and add timestamped comments. They are linked to the current playback time so you can revisit exact moments and share notes with others.'
        }
      ]
    },
    {
      category: 'Data Sources & Sensors',
      questions: [
        {
          question: '🛠 How can I use data from Brake Ace sensors?',
          answer: 'Brake Ace sensor data is supported through CSV files exported from our Brake Ace Desktop application. These files contain essential braking data that can be overlaid on your videos, giving you detailed analysis of your braking performance and overall riding dynamics.'
        },
        {
          question: '🚀 How can I use data from the BYB data acquisition system?',
          answer: 'The BYB system offers advanced telemetry including suspension data. Its CSV exports include fork and shock positions, acceleration, and gyro data. This allows you to analyze suspension behavior at various points along the track for in-depth performance analysis.'
        },
        {
          question: '🔄 How does sync between video and data work?',
          answer: 'Our system syncs video with telemetry using timestamps. When you scrub the timeline or click the chart, the video player, charts, and map all update together. If start times differ, use the sync offset in the Ride view or run settings.'
        }
      ]
    },
    {
      category: 'Calendar',
      questions: [
        {
          question: 'What does the Calendar show?',
          answer: 'The Calendar shows your events (e.g. goals and competitions with priority A/B/C), planned workouts from your training plans, and runs. You can see your week or month at a glance and drag items between days.'
        },
        {
          question: 'How do I add events or races?',
          answer: 'Add an event from the Calendar (e.g. goal or competition). You can set priority (A/B/C) and link a race type. Use the race type manager to define your race types. Events are used by training plan generation (e.g. tapering before a race).'
        },
        {
          question: 'Can I move runs and workouts between days?',
          answer: 'Yes. You can drag runs from My Runs onto the Calendar, and drag events or planned workouts between days. The Calendar updates when you drop an item on a new day.'
        },
        {
          question: 'Where do I set my training zones (HR, power, pace)?',
          answer: 'Open "Configure training zones" from the Calendar (or your profile). Set your heart rate zones, power zones, and optionally pace zones per sport. These are used in Training Plans and in the Ride view Stats panel when viewing a run.'
        }
      ]
    },
    {
      category: 'Training Plans',
      questions: [
        {
          question: 'What are Training Plans?',
          answer: 'Training Plans are date-based schedules of workouts. You choose a date range, sport, training style (e.g. polarized, pyramidal), goals, preferred days per week, and periodization. The app generates a list of structured workouts (intervals, endurance, etc.) that you can view on the Calendar and export to FIT.'
        },
        {
          question: 'How do I create a Training Plan?',
          answer: 'Go to "Training Plans" and click "Create Training Plan", or use the same action from the Calendar. Set start and end dates, sport, training style, goals, preferred training days, and periodization. The plan is generated and saved; you can expand each workout to see structure and export to FIT.'
        },
        {
          question: 'How do I use a workout (FIT export, zones)?',
          answer: 'Open a plan and expand a workout to see the full structure (warm-up, intervals, cool-down) and target intensity (power/HR/pace zones). You can export the workout to a FIT file for your device. Training zones are taken from your configured zones (see Calendar – Configure training zones).'
        }
      ]
    },
    {
      category: 'Video & Synchronization',
      questions: [
        {
          question: 'Can I use YouTube videos?',
          answer: 'Yes! You can paste a YouTube URL when creating or editing a run. The video is embedded and synchronized with your telemetry data. Ideal for sharing or analyzing existing YouTube content.'
        },
        {
          question: 'What if my video and data don\'t sync properly?',
          answer: 'Use the sync offset in the run settings or Ride view to shift video or data in time. For more control, use the Video Sync Test tool (in "Our Tools" in the sidebar) to fine-tune synchronization.'
        }
      ]
    },
    {
      category: 'Clubs & Sharing',
      questions: [
        {
          question: 'What are Clubs?',
          answer: 'Clubs are groups where you can share runs, comments, and analysis with teammates, training partners, or friends. Club members can view shared runs and participate in discussions with timestamped comments.'
        },
        {
          question: 'How do I create a Club?',
          answer: 'Club functionality is coming soon! You\'ll be able to create a club, invite members, and share your runs with specific groups. Club owners can manage members and set permissions.'
        },
        {
          question: 'Can I keep my runs private?',
          answer: 'Absolutely! By default, all runs are private. You can choose to share specific runs publicly, with specific clubs, or keep them completely private.'
        }
      ]
    },
    {
      category: 'Credits & Billing',
      questions: [
        {
          question: '💳 How are credits used?',
          answer: 'Credits are used for processing video and telemetry data, running AI analysis, GoPro telemetry extraction, and features like video export with overlays. Each task consumes a specific number of credits; your balance updates in real-time and is shown in the sidebar.'
        },
        {
          question: '🛒 How do I purchase more credits?',
          answer: 'Click "Buy Credits" at the bottom of the sidebar, or click your credit balance. Purchase is secure (Stripe) and your balance updates immediately. You can choose a package that fits your needs.'
        },
        {
          question: 'Do credits expire?',
          answer: 'No, credits never expire! Once you purchase them, they remain in your account until you use them.'
        }
      ]
    },
    {
      category: 'Tools & Other',
      questions: [
        {
          question: 'What tools are available in the sidebar?',
          answer: 'Under "Our Tools" you can find Chronocult (lap timer), Video/Data analysis (legacy multi-dashboard), Video Sync Test, and GPX Route Builder (external). Compare (admin) lets you select two runs for side-by-side analysis. Your Profile gives access to account settings, Strava/Brake Ace connection, and credits.'
        },
        {
          question: 'How do I compare two runs?',
          answer: 'Go to the "Compare" page from the sidebar (admin). Select two runs to see side-by-side metrics and map comparison.'
        },
        {
          question: 'Can I create segments?',
          answer: 'Segment creation is available in the legacy Video/Data dashboard. You can define start and end points for sections of a run to compare lap times and performance across attempts.'
        }
      ]
    },
    {
      category: 'Technical Support & Troubleshooting',
      questions: [
        {
          question: 'Why isn\'t my run displaying data?',
          answer: 'Ensure your telemetry file has valid time and sensor data (e.g. GPS, speed). Check the browser console for errors. If it persists, contact support@victorise.co with your file format and device details.'
        },
        {
          question: 'The map isn\'t showing my route',
          answer: 'This usually means the telemetry has no GPS coordinates. Ensure your GPS device had a clear view of the sky and that GPS was enabled during recording.'
        },
        {
          question: 'How can I export my analysis?',
          answer: 'You can export video with overlays from the Ride view (uses credits). Export to YouTube with overlay data and PDF reports are planned for a future release.'
        },
        {
          question: 'Is my data secure?',
          answer: 'Yes. Data is stored on Firebase with encryption. Runs are private by default and you control who can see your data.'
        }
      ]
    }
  ];

  // Filter FAQ by search: match category name, question text, or answer text (case-insensitive)
  const filteredFaqData = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) {
      return faqData.map((cat, i) => ({ ...cat, categoryIndex: i, questions: cat.questions.map((faq, j) => ({ ...faq, faqIndex: j })) }));
    }
    return faqData
      .map((cat, categoryIndex) => {
        const questions = cat.questions
          .map((faq, faqIndex) => ({ ...faq, faqIndex }))
          .filter((faq) => {
            const inCategory = cat.category.toLowerCase().includes(q);
            const inQuestion = faq.question.toLowerCase().includes(q);
            const inAnswer = faq.answer.toLowerCase().includes(q);
            return inCategory || inQuestion || inAnswer;
          });
        if (questions.length === 0) return null;
        return { category: cat.category, categoryIndex, questions };
      })
      .filter(Boolean);
  }, [searchQuery]);

  return (
    <div className="help-container">
      <div className="help-header">
        <FaQuestionCircle className="help-icon" />
        <div>
          <h1>Help Center</h1>
          <p>Find answers to common questions about Victorise</p>
        </div>
      </div>

      <div className="help-search-wrap">
        <FaSearch className="help-search-icon" aria-hidden="true" />
        <input
          type="search"
          className="help-search-input"
          placeholder="Search help (questions and answers)..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          aria-label="Search help center"
        />
      </div>

      <div className="help-faq-section">
        {searchQuery.trim() && filteredFaqData.length === 0 ? (
          <p className="help-no-results">
            No results for &quot;{searchQuery.trim()}&quot;. Try different keywords or clear the search to see all topics.
          </p>
        ) : null}
        {filteredFaqData.map((cat) => (
          <div key={cat.categoryIndex} className="help-faq-category">
            <h2 className="help-category-title">{cat.category}</h2>

            {cat.questions.map((faq) => {
              const globalIndex = `${cat.categoryIndex}-${faq.faqIndex}`;
              const isOpen = openFaqIndex === globalIndex;

              return (
                <div
                  key={faq.faqIndex}
                  className={`help-faq-item ${isOpen ? 'open' : ''}`}
                >
                  <button
                    className="help-faq-question"
                    onClick={() => toggleFaq(globalIndex)}
                  >
                    <span>{faq.question}</span>
                    {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                  </button>

                  {isOpen && (
                    <div className="help-faq-answer">
                      {faq.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div className="help-footer">
        <h3>Contact us</h3>
        <p>
          You can contact us by email for specific questions that are not covered in this Help Center.
        </p>
        <p>
          <a href="mailto:support@victorise.co">support@victorise.co</a>
        </p>
      </div>
    </div>
  );
};

export default Help;

